/**
 * 📲 **홈 화면에 추가** — 보호자 화면이 어떤 안내를 보일지 고른다 (2026-10-24 · 리서치 최종안 ③).
 *
 * ## 왜
 * 아이폰 Safari 는 사이트를 **7일 동안 안 열면** 이 기기에 적어 둔 것(히어로·읽던 쪽·보이는 방식·받아 둔 책)을
 * 지운다(WebKit 추적 방지). **홈 화면에 추가한 앱은 예외**다. 무수집이라 서버에 사본이 없으니, 지워지면 되살릴 길은
 * 종이 카드 코드뿐이다. 앞으로 만들 「부모 직접 녹음」·「책 담아 두기」도 모두 기기 저장이라 이 안내가 전제다.
 *
 * 판정은 순수 함수(`homeScreenTip`) — 화면은 그 결과만 그린다.
 */

export type HomeTip =
  /** 이미 홈 화면 앱으로 열었다 */
  | "installed"
  /** 카카오톡 등 앱 안 브라우저 — 거기서는 추가할 수 없다 */
  | "inapp"
  /** 아이폰·아이패드 Safari — 공유 → 「홈 화면에 추가」 */
  | "ios"
  /** 안드로이드 크롬이 설치 신호를 줬다 — 단추 하나로 */
  | "android-prompt"
  /** 안드로이드인데 신호가 없다 — 메뉴 ⋮ → 「홈 화면에 추가」 */
  | "android-menu"
  /** 데스크톱 등 — 안내하지 않는다 */
  | "none";

export function homeScreenTip(s: { standalone: boolean; isInApp: boolean; os: "ios" | "android" | "other"; canPrompt: boolean }): HomeTip {
  if (s.standalone) return "installed";
  if (s.isInApp) return "inapp";
  if (s.os === "ios") return "ios";
  if (s.os === "android") return s.canPrompt ? "android-prompt" : "android-menu";
  return "none";
}

/** 지금 홈 화면 앱(standalone)으로 열려 있나 — 아이폰은 `navigator.standalone`, 나머지는 display-mode */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia?.("(display-mode: standalone)").matches === true;
}

/** 크롬의 설치 신호(표준 타입에 없다) */
export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let stashed: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((f) => f());

/**
 * 🔴 **앱이 뜨자마자 듣는다**(main.tsx 한 곳). 설치 신호는 페이지가 뜰 때 **한 번만** 온다 —
 * 보호자 화면이 열릴 때 듣기 시작하면 이미 지나가 있다(메모리 listener-mounts-too-late: 상태형만 다시 준다).
 * 받아 두고, 보호자 화면이 단추를 눌렀을 때 쓴다. 크롬이 띄우는 작은 안내 띠는 막는다 — 아이 화면에 불쑥 뜨지 않게.
 */
export function captureInstallPrompt(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    stashed = e as InstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    stashed = null;
    notify();
  });
}

export function canPromptInstall(): boolean {
  return stashed !== null;
}

/** 단추를 눌렀을 때 — 크롬의 설치 창을 띄운다. 한 번 쓴 신호는 다시 못 쓴다(크롬 규칙) */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const e = stashed;
  if (!e) return "unavailable";
  stashed = null;
  notify();
  await e.prompt();
  return (await e.userChoice).outcome;
}

export function onInstallPromptChange(f: () => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}
